// ── Analysis Types ──────────────────────────────────────

export type AnalysisType =
  | 'brand_name'
  | 'slogan'
  | 'concept'
  | 'label'
  | 'campaign'
  | 'social_post'
  | 'general_question'
  | 'creative_brief'
  | 'event_activation';

export interface AnalysisOption {
  key: AnalysisType;
  label: string;
  description: string;
  icon: string;
  inputType: 'text' | 'image' | 'text+image';
  proOnly?: boolean;
}

export const ANALYSIS_OPTIONS: AnalysisOption[] = [
  {
    key: 'general_question',
    label: 'Question générale',
    description: 'Posez une question sur la Loi Évin et la réglementation alcool',
    icon: '❓',
    inputType: 'text',
  },
  {
    key: 'brand_name',
    label: 'Nom de marque',
    description: 'Vérifiez la conformité d\'un nom de marque ou de produit',
    icon: '🏷️',
    inputType: 'text',
  },
  {
    key: 'slogan',
    label: 'Slogan / Accroche',
    description: 'Analysez un slogan ou une accroche publicitaire',
    icon: '💬',
    inputType: 'text',
  },
  {
    key: 'concept',
    label: 'Audit de concept',
    description: 'Vous avez déjà une idée ? Faites-la analyser par Evinly',
    icon: '💡',
    inputType: 'text+image',
  },
  {
    key: 'label',
    label: 'Étiquette',
    description: 'L\'étiquette en rayon n\'est pas soumise à la Loi Évin. Elle le devient dès qu\'elle est photographiée, partagée ou reproduite en communication : cette analyse évalue ce risque.',
    icon: '🍾',
    inputType: 'image',
  },
  {
    key: 'campaign',
    label: 'Campagne publicitaire',
    description: 'Évaluez des visuels de campagne publicitaire',
    icon: '📢',
    inputType: 'image',
  },
  {
    key: 'social_post',
    label: 'Post réseaux sociaux',
    description: 'Analysez un post Instagram, LinkedIn, TikTok ou autre réseau social',
    icon: '📱',
    inputType: 'text',
  },
  {
    key: 'event_activation',
    label: 'Événement / PLV',
    description: 'Auditez une opération trade : goodies, stand, PLV, affichettes, dégustation, personnel',
    icon: '🎪',
    inputType: 'text+image',
  },
  {
    key: 'creative_brief',
    label: 'Évin Créa',
    description: 'Pas encore d\'idée ? Notre IA génère 6 concepts conformes à partir de votre brief',
    icon: '🎨',
    inputType: 'text',
  },
];

// ── Analysis Input ──────────────────────────────────────

// ── Label Usage (only for type === 'label') ────────────
// Declares the intended use of the label, which dictates whether
// Loi Évin applies (it does NOT apply to the physical packaging in-store,
// but DOES apply as soon as the label is reproduced in communication).

export type LabelUsage =
  | 'rayon'      // Étiquette physique en rayon uniquement — hors Loi Évin
  | 'packshot'   // Photo produit (e-commerce, catalogue, presse) — Loi Évin s'applique
  | 'campaign'   // Reproduction dans une campagne publicitaire — Loi Évin pleine
  | 'undecided'; // Usage non déterminé — audit prudentiel (assume le pire cas)

export const LABEL_USAGE_OPTIONS: { value: LabelUsage; label: string; description: string }[] = [
  {
    value: 'undecided',
    label: 'Je ne sais pas encore / audit prudentiel',
    description: 'Analyse tous les risques au cas où l\'étiquette serait un jour photographiée ou diffusée.',
  },
  {
    value: 'rayon',
    label: 'Étiquette physique uniquement (en rayon / caveau)',
    description: 'Aucune photo ni reproduction prévue. La Loi Évin ne s\'applique pas directement.',
  },
  {
    value: 'packshot',
    label: 'Photo produit (e-commerce, catalogue, presse)',
    description: 'L\'étiquette sera photographiée et diffusée. La Loi Évin s\'applique pleinement.',
  },
  {
    value: 'campaign',
    label: 'Reproduction dans une campagne publicitaire',
    description: 'L\'étiquette sera intégrée dans des visuels publicitaires. Vigilance maximale.',
  },
];

export interface AnalysisInput {
  type: AnalysisType;
  textContent?: string;
  images?: ImageInput[];
  context?: string; // optional user-provided context
  /** Social post metadata (only when type === 'social_post') */
  social?: SocialPostMeta;
  /** Declared usage for the label (only when type === 'label'). Defaults to 'undecided'. */
  labelUsage?: LabelUsage;
  /** Event/PLV metadata (only when type === 'event_activation') */
  event?: EventActivationMeta;
}

// ── Event / PLV specifics ──────────────────────────────
// An event "activation" bundles several supports (goodies, stand, PLV,
// dégustation, personnel) that each carry distinct Loi Évin constraints:
// - Goodies are tightly restricted (L3323-6: pro circuit only).
// - PLV and affichettes on the point of sale have their own regime.
// - Dégustation rules differ between pro salons and grand public.
// The audience field gates the regime applied by the pipeline.

export type EventAudience =
  | 'pro_only'      // Salon B2B fermé, CHR, cavistes — régime assoupli (goodies, échantillons)
  | 'grand_public'  // Consommateurs finaux — Loi Évin pleine
  | 'mixte'         // Salon ouvert au public : régime grand public par prudence
  | 'undecided';    // Audit prudentiel (worst case = grand public)

export const EVENT_AUDIENCE_OPTIONS: { value: EventAudience; label: string; description: string }[] = [
  {
    value: 'pro_only',
    label: 'Public professionnel uniquement (CHR, cavistes, pros du vin)',
    description: 'Salon B2B fermé, événement trade. Régime assoupli : goodies et échantillonnage acceptés dans le circuit pro.',
  },
  {
    value: 'grand_public',
    label: 'Grand public',
    description: 'Consommateurs finaux. Loi Évin pleine : goodies interdits (L3323-6), dégustation encadrée.',
  },
  {
    value: 'mixte',
    label: 'Mixte (pro + grand public)',
    description: 'Salon ouvert au public, portes ouvertes, journée découverte. Régime grand public appliqué par prudence.',
  },
  {
    value: 'undecided',
    label: 'Je ne sais pas encore / audit prudentiel',
    description: 'Analyse au pire cas — régime grand public appliqué par défaut.',
  },
];

export type EventSupport =
  | 'goodies'           // Objets promotionnels (verres, tire-bouchons, textiles…)
  | 'plv'               // PLV : stop-rayon, kakémono, tête de gondole
  | 'stand'             // Stand / décor d'espace (salon, événement)
  | 'affichage_local'   // Affichettes, pancartes sur le lieu de l'opération
  | 'signaletique'      // Oriflammes, bâches, totems, habillage extérieur
  | 'degustation'       // Dégustation / échantillonnage / service
  | 'personnel'         // Tenues, scripts, sollicitation hôtesses
  | 'menu_carte'        // Carte/menu sur le lieu (bar, restaurant, caveau)
  | 'autre';

export const EVENT_SUPPORT_OPTIONS: { value: EventSupport; label: string; description: string }[] = [
  {
    value: 'goodies',
    label: 'Objets promotionnels (goodies)',
    description: 'Verres, tire-bouchons, sacs, textiles portant la marque d\'alcool.',
  },
  {
    value: 'plv',
    label: 'PLV (publicité sur le lieu de vente)',
    description: 'Stop-rayon, kakémono, tête de gondole, présentoir en magasin de boissons.',
  },
  {
    value: 'stand',
    label: 'Stand / décor d\'espace',
    description: 'Décor, mobilier, scénographie d\'un stand (salon, événement).',
  },
  {
    value: 'affichage_local',
    label: 'Affichettes / pancartes',
    description: 'Affiches imprimées sur le lieu de l\'opération (caveau, salon, bar partenaire).',
  },
  {
    value: 'signaletique',
    label: 'Signalétique événement',
    description: 'Oriflammes, bâches, totems, habillage extérieur.',
  },
  {
    value: 'degustation',
    label: 'Dégustation / service',
    description: 'Dégustation gratuite ou payante, échantillonnage, service par hôte·sses.',
  },
  {
    value: 'personnel',
    label: 'Personnel / hôtesses',
    description: 'Tenue, script d\'accueil, sollicitation active du public.',
  },
  {
    value: 'menu_carte',
    label: 'Carte / menu',
    description: 'Mise en avant sur carte de bar, restaurant, caveau partenaire.',
  },
  {
    value: 'autre',
    label: 'Autre support',
    description: 'Décrivez dans la zone "Contexte" ci-dessous.',
  },
];

export interface EventActivationMeta {
  /** Who the operation targets — drives which legal regime applies. */
  audience: EventAudience;
  /** Supports present in the operation. Used by the pipeline to surface
   *  the applicable articles (L3323-6 for goodies, ARPP for PLV, etc.). */
  supports: EventSupport[];
  /** Free-text explanation per checked support (pourquoi, comment, combien,
   *  dans quel but). Gives the judges the concrete facts they need to judge
   *  — the difference between "goodie générique" and "100 tire-bouchons
   *  distribués gratuitement aux visiteurs" can flip the verdict. */
  supportDetails?: Partial<Record<EventSupport, string>>;
  /** Optional event context. */
  eventName?: string;
  eventLocation?: string;
  eventDate?: string;
  /** Free-text description of the operation. */
  operationBrief?: string;
}

// ── Social Post specifics ──────────────────────────────

export type SocialMode = 'analysis';

export type SocialFormat = 'post' | 'carousel' | 'video';

export const SOCIAL_FORMAT_LABELS: Record<SocialFormat, string> = {
  post: 'Publication',
  carousel: 'Carrousel',
  video: 'Story / Reel vidéo',
};

export type SocialPlatform = 'instagram' | 'linkedin' | 'facebook';

export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
};

export interface SocialPostMeta {
  mode: SocialMode;
  format?: SocialFormat;
  platform?: SocialPlatform;
  caption?: string;
  hashtags?: string[];
  mentions?: string[];
}

export interface ImageInput {
  base64: string;
  mimeType: string;
  filename: string;
}

// ── Analysis Result ─────────────────────────────────────

/** Avertissement hors périmètre Loi Évin (religion / politique / conflit armé). */
export interface OutOfScopeFlag {
  topic: 'religion' | 'politique' | 'conflit';
  /** L'élément concerné (nom, mot, symbole détecté). */
  element: string;
  /** Pourquoi c'est signalé, et que ça ne relève pas de la Loi Évin. */
  explanation: string;
  /** Base juridique du risque distinct (ex. « Article L711-2 CPI »). */
  legalBasis: string;
}

export interface EvinAnalysisResult {
  score: number; // 0-100
  verdict: 'CONFORME' | 'VIGILANCE' | 'RISQUE_ELEVE' | 'NON_CONFORME';
  summary: string;

  conformePoints: ConformePoint[];
  pointsDeVigilance: PointDeVigilance[];
  interdictionsFormelles: InterdictionFormelle[];
  sources: SourceCitation[];

  // Pipeline metadata
  judgeDefenseScore: number;
  judgeContradicteurScore: number;
  arbitrageDetails: string;

  /** Signaux HORS périmètre Loi Évin (religion / politique / conflit) — n'affectent
   *  PAS le score Loi Évin. Avertissent d'un risque distinct (dépôt de marque
   *  contraire à l'ordre public, art. L711-2 CPI ; risque d'image). Souvent vide. */
  outOfScopeFlags?: OutOfScopeFlag[];

  /** Free-form answer for general_question type (no scored pipeline) */
  generalAnswer?: string;

  /** Original user input preserved for later reference */
  originalInput?: string;
  /** Optional context provided by the user */
  originalContext?: string;
  /** Original images as data URLs for later reference */
  originalImages?: string[];
  /** Usage déclaré pour les analyses d'étiquette — affiché dans le récap. */
  originalLabelUsage?: LabelUsage;
  /** Métadonnées événement/PLV déclarées (audience, supports, détails) — affichées dans le récap. */
  originalEvent?: EventActivationMeta;

  /** Social metadata echoed back for UI display */
  socialMeta?: SocialPostMeta;

  /** AI-generated compliant rewrite (if user requests fix auto) */
  suggestedFix?: SuggestedFix;

  /** Langfuse trace ID — enables tying user feedback (thumbs up/down)
   *  back to the exact LLM run that produced this result. */
  traceId?: string;

  /** Évin Créa: generated creative ideas (only for creative_brief type) */
  creativeResult?: CreativeResult;
}

// ── Suggested Fix (auto-rewrite) ───────────────────────

export interface SuggestedFix {
  /** The compliant rewritten content */
  rewritten: string;
  /** What changed and why (bullet points) */
  changes: Array<{ before: string; after: string; reason: string }>;
  /** Overall explanation */
  rationale: string;
}

export interface SuggestFixRequest {
  analysisType: AnalysisType;
  originalContent: string;
  issues: Array<{ element: string; explanation: string; legalBasis?: string }>;
  context?: string;
}

// ── Évin Créa — Brief créatif & idées conformes ──────

export type CreativeChannel =
  | 'affichage'
  | 'digital'
  | 'social'
  | 'print'
  | 'tv'
  | 'evenement'
  | 'packaging'
  | 'autre';

export const CREATIVE_CHANNEL_OPTIONS: { value: CreativeChannel; label: string }[] = [
  { value: 'social', label: 'Réseaux sociaux' },
  { value: 'digital', label: 'Digital (bannières, site)' },
  { value: 'affichage', label: 'Affichage (4x3, DOOH)' },
  { value: 'print', label: 'Print (presse, magazine)' },
  { value: 'tv', label: 'TV / Vidéo' },
  { value: 'evenement', label: 'Événementiel' },
  { value: 'packaging', label: 'Packaging / PLV' },
  { value: 'autre', label: 'Autre' },
];

export interface CreativeBriefInput {
  brand: string;
  product: string;
  positioning?: string;
  target: string;
  channel: CreativeChannel;
  message: string;
  constraints?: string;
}

export interface CreativeIdea {
  territory: string;
  concept: string;
  slogans: string[];
  visualDirection: string;
  legalBasis: string[];
  riskScore: number; // 0-100 (higher = safer)
  riskLabel: 'Vert' | 'Jaune';
  executionNotes: string;
}

export interface CreativeResult {
  ideas: CreativeIdea[];
  legalFramework: string;
  sources: SourceCitation[];
  briefEcho: CreativeBriefInput;
}

export interface ConformePoint {
  element: string;
  legalBasis: string;
  explanation: string;
}

export interface PointDeVigilance {
  element: string;
  riskLevel: 'Faible' | 'Moyen' | 'Élevé';
  legalBasis: string;
  jurisprudence?: string;
  explanation: string;
  recommendation: string;
}

export interface InterdictionFormelle {
  element: string;
  legalBasis: string;
  explanation: string;
}

export interface SourceCitation {
  type: 'LOI' | 'JURISPRUDENCE' | 'ARPP' | 'DOCTRINE';
  title: string;
  reference: string;
  /** Full article text fetched from Légifrance (expandable in UI) */
  articleContent?: string;
  /** Date of the article or decision */
  date?: string;
}

// ── RAG Documents ───────────────────────────────────────

export type DocumentCategory =
  | 'TEXTE_DE_LOI'
  | 'JURISPRUDENCE'
  | 'DOCTRINE_AVOCAT'
  | 'REGLEMENTATION_OFFICIELLE'
  | 'ARPP'
  | 'EXEMPLE_REEL';

export type VerdictTag =
  | 'AUTORISE'
  | 'INTERDIT'
  | 'ZONE_GRISE'
  | 'CONFORME'
  | 'NON_CONFORME';

export interface EvinDocument {
  id: string;
  title: string;
  content: string;
  summary: string;
  category: DocumentCategory;
  verdictTag: VerdictTag;
  analysisTypes: AnalysisType[]; // which analysis types this doc is relevant for
  legalReferences: string[]; // e.g. ["L3323-2", "L3323-4"]
  weight: number; // 0.0-1.0 — relevance weighting
  embedding?: number[];
  createdAt: string;
  updatedAt: string;
}

// ── Pipeline Internal Types ─────────────────────────────

export interface FactualExtraction {
  type: AnalysisType;
  detectedElements: string[];
  textualContent: string[];
  visualElements?: string[];
  ambiance?: string;
  healthWarningPresent?: boolean;
  targetAudience?: string;
  rawDescription: string;
  /**
   * Contexte brut saisi par l'utilisateur (ex : "boisson à base de vin 7%,
   * légère, pétillante"). Ce champ sert à la COMPRÉHENSION du produit par les
   * juges, il NE DOIT PAS être analysé pour sa conformité ni apparaître dans
   * `textualContent` ou `detectedElements`. Il ne peut pas générer de points
   * de vigilance ni d'interdictions.
   */
  productMetadata?: string;
  /**
   * Figures de style détectées dans le texte analysé (jeux de mots,
   * homophones, contractions, allitérations, références culturelles,
   * néologismes). Utilisé pour `brand_name`, `slogan`, `concept`.
   * Le contradicteur applique la grille de gravité selon le sens implicite
   * (rite soft → Faible ; fête/sport/séduction → Moyen/Élevé ;
   * ivresse/excès → Interdiction formelle ; univers enfantin → Interdiction).
   */
  linguisticDevices?: Array<{
    type:
      | 'jeu_de_mot'
      | 'homophone'
      | 'contraction'
      | 'alliteration'
      | 'reference_culturelle'
      | 'neologisme';
    surface: string;
    impliedMeaning: string;
    evinRelevance: string;
    gravityHint: 'anodin' | 'faible' | 'moyen' | 'eleve' | 'interdiction';
  }>;
  /** Optional social-specific signals (oui/non + description) */
  socialSignals?: {
    cartoonCharacters: boolean;
    cartoonDescription?: string;
    apparentAgeRange: string;
    minorsUniverse: boolean;
    minorsUniverseDescription?: string;
    candyObjects: boolean;
    candyDescription?: string;
    childishEmojis: string[];
    personsConsuming: boolean;
    riskyAssociations: string[];
    hashtags: string[];
    mentions: string[];
    commercialDisclosure: boolean;
    commercialDisclosureText?: string;
  };
}

export interface JudgeVerdict {
  role: 'defense' | 'contradicteur';
  score: number;
  arguments: JudgeArgument[];
  conclusion: string;
}

export interface JudgeArgument {
  element: string;
  position: 'conforme' | 'non_conforme' | 'zone_grise';
  legalBasis: string;
  reasoning: string;
}

// ── API Request/Response ────────────────────────────────

export interface AnalyzeRequest {
  type: AnalysisType;
  textContent?: string;
  context?: string;
  // images sent as multipart form data
}

export interface AnalyzeResponse {
  success: boolean;
  result?: EvinAnalysisResult;
  error?: string;
  /** UUID de la ligne persistée dans evin_analyses (renvoyé dans l'event SSE
   *  `done` quand l'utilisateur a la feature history). Permet de relier une
   *  analyse fraîche à sa ligne serveur — indispensable en mode concurrent. */
  analysisId?: string;
}

// ── History ─────────────────────────────────────────────

export interface AnalysisHistoryItem {
  id: string; // UUID serveur (evin_analyses.id) ou crypto.randomUUID() local
  title: string;
  analysisType: AnalysisType;
  analysisLabel: string;
  verdict: 'CONFORME' | 'VIGILANCE' | 'RISQUE_ELEVE' | 'NON_CONFORME';
  score: number;
  timestamp: string; // ISO string
  /** The input that was analyzed (text or image filenames) */
  inputSummary: string;
  /** Full result for re-display */
  result: EvinAnalysisResult;
}

// ── Server-persisted analyses (Historique) ────────────

export type AnalysisActionTaken =
  | 'pending'
  | 'published'
  | 'published_with_fix'
  | 'discarded';

export interface ServerAnalysisRecord {
  id: string;
  userId: string | null;
  organizationId: string | null;
  analysisType: AnalysisType;
  socialMode: SocialMode | null;
  platform: SocialPlatform | null;
  caption: string | null;
  hashtags: string[];
  mentions: string[];
  imageUrl: string | null;
  title: string | null;
  verdict: EvinAnalysisResult['verdict'] | null;
  score: number | null;
  defenseScore: number | null;
  contradicteurScore: number | null;
  suggestedFix: string | null;
  actionTaken: AnalysisActionTaken;
  userTag: string | null;
  publishedAt: string | null;
  createdAt: string;
  /** B1 — cycle de vie du job (running au démarrage → done/error à la fin). */
  status: 'running' | 'done' | 'error';
  progress: number | null;
  progressLabel: string | null;
  errorMessage: string | null;
  /** The full result (for re-display) */
  result: EvinAnalysisResult | null;
}

export interface AnalysesListFilters {
  period?: '7d' | '30d' | '90d' | 'all';
  verdict?: EvinAnalysisResult['verdict'];
  platform?: SocialPlatform;
  socialMode?: SocialMode;
  analysisType?: AnalysisType;
  userTag?: string;
  actionTaken?: AnalysisActionTaken;
}

export interface AnalysesStats {
  totalCount: number;
  conformeCount: number;
  vigilanceCount: number;
  risqueEleveCount: number;
  nonConformeCount: number;
  fixedCount: number;
  avgScore: number | null;
  lastAnalysisAt: string | null;
}

// ── LLM quality feedback (Langfuse scores) ─────────────

export type AnalysisFeedbackValue = 'positive' | 'negative';

export interface AnalysisFeedbackPayload {
  traceId: string;
  value: AnalysisFeedbackValue;
  comment?: string;
}

// ── Auth & Billing ─────────────────────────────────────

export type OrgRole = 'owner' | 'admin' | 'member';
// `partner` = accès illimité OFFERT (équivalent Premium, 0 €), réservé aux
// partenaires contributeurs (ex. avocats) en échange de retours. Activé depuis
// le dashboard SuperAdmin. N'apparaît jamais dans PRICING_TIERS (non achetable).
export type PlanType = 'free' | 'beta' | 'pro' | 'premium' | 'enterprise' | 'partner';
export type SubscriptionStatus =
  | 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid'
  | 'incomplete' | 'incomplete_expired' | 'paused' | null;

/** Statuts Stripe d'un abonnement dont le premier paiement n'est PAS confirmé.
 *  Tant que l'abonnement est dans l'un d'eux, aucun plan payant n'est accordé
 *  (cf. server/services/stripeGuards.ts) et l'org reste « sans abonnement ». */
export const UNCONFIRMED_SUBSCRIPTION_STATUSES = ['incomplete', 'incomplete_expired'] as const;

export function isSubscriptionPaymentUnconfirmed(status: string | null | undefined): boolean {
  return !!status && (UNCONFIRMED_SUBSCRIPTION_STATUSES as readonly string[]).includes(status);
}

export interface UserProfile {
  id: string;
  email: string;
  displayName: string | null;
  jobTitle: string | null;
  industry: string | null;
  avatarUrl: string | null;
  orgRole: OrgRole;
  organizationId: string;
  plan: PlanType;
  isSuperAdmin: boolean;
  mfaEnrolled: boolean;
  /** La double authentification peut-elle être activée sur cette instance ?
   *  (clé de chiffrement MFA_ENCRYPTION_KEY présente côté serveur). */
  mfaAvailable?: boolean;
  /** Code promo posé à l'inscription et pas encore encaissé — porteur qui
   *  survit à l'ouverture de session dans un autre navigateur. */
  pendingPromoCode?: string | null;
  createdAt: string | null;
  termsAcceptedAt: string | null;
  termsVersion: string | null;
}

// Métier et secteur d'activité : demandés à l'inscription (obligatoires) puis
// modifiables dans les paramètres du compte. Listes fermées : le serveur refuse
// toute autre valeur (POST /api/auth/signup et /api/auth/profile). Les anciennes
// valeurs libres des comptes existants restent lisibles et conservées tant que
// l'utilisateur n'en choisit pas une nouvelle.
export const JOB_OPTIONS = [
  'Direction ou fondateur',
  'Marketing et communication',
  'Direction artistique ou graphisme',
  'Commercial ou export',
  'Juridique et conformité',
  'Community management',
  'Chef de produit',
  'Autre',
] as const;

export type JobTitle = typeof JOB_OPTIONS[number];

export const INDUSTRY_OPTIONS = [
  'Brasserie',
  'Vin et vignoble',
  'Spiritueux et distillerie',
  'Cidre et poiré',
  'Boissons sans alcool',
  'Agence ou studio de design',
  'Distribution, caviste ou CHR',
  'Événementiel',
  'Média',
  'Juridique ou conseil',
  'Autre',
] as const;

export type Industry = typeof INDUSTRY_OPTIONS[number];

export function isJobOption(value: unknown): value is JobTitle {
  return typeof value === 'string' && (JOB_OPTIONS as readonly string[]).includes(value);
}

export function isIndustryOption(value: unknown): value is Industry {
  return typeof value === 'string' && (INDUSTRY_OPTIONS as readonly string[]).includes(value);
}

/** Vrai quand le profil doit encore être complété (métier ou secteur vide) :
 *  inscriptions Google, par code, et comptes créés avant que ces champs
 *  deviennent obligatoires. Une ancienne valeur libre compte comme remplie. */
export function profileNeedsCompletion(
  profile: { jobTitle: string | null; industry: string | null } | null | undefined,
): boolean {
  if (!profile) return false;
  return !profile.jobTitle?.trim() || !profile.industry?.trim();
}

/** Facture Stripe de l'org, telle que listée dans Paramètres > Facturation. */
export interface BillingInvoice {
  id: string;
  number: string | null;
  /** Date d'émission, ISO 8601. */
  createdAt: string;
  amountCents: number;
  currency: string;
  status: 'paid' | 'open';
  /** Page Stripe de la facture (consultation, paiement si ouverte). */
  hostedUrl: string | null;
  /** Lien direct vers le PDF. */
  pdfUrl: string | null;
}

export interface Organization {
  id: string;
  name: string;
  slug: string | null;
  plan: PlanType;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscriptionStatus: SubscriptionStatus;
  currentPeriodEnd: string | null;
  freeAnalysesUsed: number;
  freeAnalysesLimit: number;
  creditBalance: number;
  /** Crédits disponibles par type d'analyse (agrégés depuis org_credits).
   *  Débloque une tuile même quand le plan ne donne pas accès au type. */
  creditsPerType?: Partial<Record<AnalysisType, number>>;
  maxMembers: number;
  /** Per-org gate on the public API. Set by super-admin only. */
  apiAccessEnabled: boolean;
}

// ── API Keys ────────────────────────────────────────────

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  environment: 'live' | 'test';
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** Returned only at creation time — `plainKey` is shown ONCE to the user. */
export interface CreatedApiKey extends ApiKey {
  plainKey: string;
}

// ── Admin: organization listing ────────────────────────

export interface AdminOrganization {
  id: string;
  name: string;
  plan: PlanType;
  apiAccessEnabled: boolean;
  createdAt: string;
  /** Email du propriétaire — identifie le compte depuis le dashboard admin.
   *  `null` si l'org n'a pas de membre `owner`, ou pas de profil rattaché. */
  ownerEmail: string | null;
  /** Pool d'analyses incluses. Ne concerne que les plans `free` et `beta` :
   *  les plans illimités ignorent ces compteurs. Restant = limit - used. */
  freeAnalysesUsed: number;
  freeAnalysesLimit: number;
  /** Code promo échangé par cette org, `null` si aucun. Identifie d'un coup
   *  d'œil les comptes venus d'une carte de visite salon — et, par recoupement
   *  des dates d'inscription, une éventuelle série d'adresses jetables. */
  promoCode: string | null;
  /** Suspension : `null` = org active. Coupe l'accès aux routes métier sans
   *  rien détruire — réversible, contrairement à la suppression. */
  suspendedAt: string | null;
  suspendedReason: string | null;
  /** Aucun membre : le compte propriétaire a été effacé (droit à l'oubli ou
   *  purge d'inactivité) et l'org ne survit que comme trace comptable,
   *  renommée « [Compte supprimé …] ». Le dashboard n'y propose que la purge. */
  memberless: boolean;
}

/** Une ligne du rapport de cohorte d'un code promo — un compte servi. */
export interface PromoCohortRow {
  organizationId: string;
  email: string | null;
  redeemedAt: string;
  /** Canal d'entrée : QR scanné, saisie à l'inscription, ou rattrapage manuel. */
  source: string;
  analysesGranted: number;
  analysesUsed: number;
  /** Répartition des analyses réellement lancées, par type. */
  byType: Record<string, number>;
  lastActivityAt: string | null;
  suspended: boolean;
}

/** Rapport complet d'un code promo — alimente la vue et l'export PDF. */
export interface PromoCohortReport {
  code: string;
  label: string;
  analysesPerAccount: number;
  generatedAt: string;
  accounts: number;
  /** Comptes ayant lancé au moins une analyse — le vrai indicateur de
   *  conversion d'un salon : une inscription qui ne teste rien ne vaut rien. */
  activated: number;
  analysesGranted: number;
  analysesUsed: number;
  byType: Record<string, number>;
  rows: PromoCohortRow[];
}

/** Réglage du pool d'analyses bêta depuis le dashboard admin.
 *  `set` → le restant devient exactement `amount` ; `add` → ajoute `amount`
 *  au pool (négatif pour retirer). Voir POST /api/admin/orgs/:id/beta-credits. */
export type BetaCreditsMode = 'set' | 'add';

/** Garde-fou partagé client/serveur sur le pool d'analyses bêta. */
export const MAX_BETA_CREDITS = 1000;

/**
 * Nouveau `free_analyses_limit` d'une org bêta après un réglage admin.
 *
 * Le pool bêta est un compteur `used` / `limit` : le restant vaut
 * `limit - used` (cf. la RPC `reserve_free_analysis`, qui gate sur
 * `used + cost <= limit`).
 *
 * On raisonne toujours en RESTANT, jamais en `limit` brut, car `limit` peut
 * être INFÉRIEUR à `used` : repasser une org en `free` via POST /orgs/:id/plan
 * met `limit = 0` sans toucher `used`. Sur un tel état (used=25, limit=0), un
 * `add(+10)` calculé sur `limit` donnerait `max(used, 0+10) = 25`, soit 0
 * restante — le bouton « +10 » n'accorderait rien. En passant par le restant,
 * `add(+10)` accorde bien 10 analyses, quel que soit l'état d'où l'on part.
 *
 *   • `set` → le restant devient exactement `amount`
 *   • `add` → le restant devient `restant + amount` (négatif = retrait)
 *
 * Invariants (source unique de vérité, testée) :
 *   • restant jamais négatif — on ne reprend pas une analyse déjà consommée,
 *     donc `limit` ne descend jamais sous `used` ;
 *   • restant plafonné à MAX_BETA_CREDITS — garde-fou anti-faute de frappe
 *     (un « 10000 » au lieu de « 100 » n'offre pas la plateforme).
 */
export function computeBetaLimit(
  mode: BetaCreditsMode,
  amount: number,
  used: number,
  currentLimit: number,
): number {
  const currentRemaining = Math.max(0, currentLimit - used);
  const targetRemaining = mode === 'set' ? amount : currentRemaining + amount;
  const nextRemaining = Math.min(Math.max(0, targetRemaining), MAX_BETA_CREDITS);
  return used + nextRemaining;
}

// ── Codes promo (cartes de visite salon / outbound) ────
//
// Un code dépose N analyses dans le POOL BÊTA de l'organisation — le même
// compteur que le bouton admin « +10 » ci-dessus. Le calcul vit côté SQL
// (RPC `redeem_promo_code`, supabase-schema-promo-codes.sql), qui reproduit
// exactement `computeBetaLimit('add', …)` : restant plafonné à
// MAX_BETA_CREDITS, jamais négatif, jamais de recharge des analyses consommées.

/** Nom du paramètre d'URL porté par le QR code des cartes de visite.
 *  Court volontairement : il finit encodé dans le QR, chaque caractère
 *  économisé allège la matrice et rend le scan plus tolérant. */
export const PROMO_CODE_PARAM = 'c';

export const PROMO_CODE_MAX_LENGTH = 32;

/**
 * Met un code promo sous sa forme canonique — MAJUSCULES, alphanumérique.
 *
 * Source unique client/serveur : le même code doit matcher qu'il soit lu dans
 * l'URL du QR, tapé à la main depuis la carte (« exemple10 »), ou collé avec
 * une espace de trop. Tout ce qui n'est pas [A-Z0-9] est retiré plutôt que
 * rejeté : un scan approximatif ou un copier-coller qui embarque un guillemet
 * ne doit pas coûter une inscription au salon.
 *
 * Renvoie '' si rien d'exploitable ne reste — les appelants traitent la chaîne
 * vide comme « pas de code », jamais comme un code invalide à signaler.
 */
export function normalizePromoCode(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return '';
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, PROMO_CODE_MAX_LENGTH);
}

/**
 * Forme canonique d'une adresse email, pour empêcher qu'un même individu
 * encaisse plusieurs fois le même code promo derrière des alias.
 *
 * Deux règles, volontairement inégales :
 *   • le **sous-adressage** (`nom+salon@domaine`) est retiré pour TOUS les
 *     domaines — la convention `+suffixe` est quasi universelle (Gmail,
 *     Outlook, Fastmail, Proton…) et n'a jamais d'autre sens que « même
 *     boîte, libellé différent » ;
 *   • les **points** ne sont retirés que sur Gmail, seul fournisseur courant
 *     à les ignorer. Les enlever partout fusionnerait à tort des adresses
 *     bel et bien distinctes (`jean.dupont@` ≠ `jeandupont@` ailleurs), et
 *     refuserait le code à un collègue innocent.
 *
 * Sert uniquement à la déduplication des échanges : ce n'est jamais l'adresse
 * à laquelle on écrit, seulement une clé de comparaison.
 *
 * Renvoie '' si l'entrée n'est pas une adresse exploitable — l'appelant traite
 * alors le cas comme « pas de déduplication possible » plutôt que d'inventer
 * une clé, pour ne jamais bloquer un échange légitime sur une adresse exotique.
 */
export function normalizeEmailForDedup(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim().toLowerCase();

  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return '';

  let local = trimmed.slice(0, at);
  let domain = trimmed.slice(at + 1);
  if (domain.includes('@') || !domain.includes('.')) return '';

  const plus = local.indexOf('+');
  if (plus > 0) local = local.slice(0, plus);

  if (domain === 'googlemail.com') domain = 'gmail.com';
  if (domain === 'gmail.com') local = local.replace(/\./g, '');

  if (!local) return '';
  return `${local}@${domain}`;
}

/** Issues possibles d'un échange (miroir des statuts renvoyés par la RPC). */
export type PromoRedeemStatus =
  | 'ok'
  | 'not_found'
  | 'expired'
  | 'exhausted'
  | 'already_redeemed'
  /** Une autre adresse de la même boîte a déjà encaissé ce code
   *  (cf. normalizeEmailForDedup) — tentative de farming par alias. */
  | 'email_already_redeemed'
  | 'plan_not_eligible'
  | 'org_not_found';

/** Ce qu'un visiteur non authentifié peut savoir d'un code (GET /api/promo/:code).
 *  Volontairement pauvre : ni compteur d'utilisations, ni plafond. */
export interface PromoCodePublicInfo {
  code: string;
  /** null quand le code est inconnu ou inactif — on ne confirme pas son existence. */
  label: string | null;
  /** Nombre d'analyses offertes, null si le code n'est pas échangeable. */
  analyses: number | null;
  valid: boolean;
}

export interface PromoRedeemSuccess {
  code: string;
  label: string;
  /** Analyses réellement créditées (peut être < `analyses` si le plafond
   *  MAX_BETA_CREDITS était déjà proche — cas d'école, mais on dit la vérité). */
  analysesGranted: number;
  /** Total restant dans le pool après l'échange. */
  analysesRemaining: number;
}

/** Vue owner d'un code (GET /api/admin/promo-codes) — avec les compteurs. */
export interface AdminPromoCode {
  code: string;
  label: string;
  analyses: number;
  expiresAt: string | null;
  maxRedemptions: number | null;
  redemptionsCount: number;
  active: boolean;
  createdAt: string;
  /** Échangeable ici et maintenant (actif + non expiré + plafond non atteint). */
  valid: boolean;
}

/** Création / mise à jour d'un code (POST /api/admin/promo-codes).
 *  Sert aussi à prolonger une campagne : même code, nouvelle `expiresAt` et
 *  nouveau `maxRedemptions` — les cartes déjà imprimées restent valables. */
export interface PromoCodeInput {
  code: string;
  label: string;
  analyses: number;
  expiresAt: string | null;
  maxRedemptions: number | null;
  active: boolean;
}

export interface OrgMember {
  id: string;
  userId: string;
  email: string;
  displayName: string | null;
  role: OrgRole;
  joinedAt: string;
}

// ── Plan Features (drives UI gating) ──────────────────

export interface PlanFeatures {
  plan: PlanType;
  analysesText: boolean;
  analysesLabel: boolean;
  analysesCampaign: boolean;
  analysesSocial: boolean;
  analysesCreative: boolean;
  analysesEvent: boolean;
  fullResults: boolean;
  history: boolean;
  exportPdf: boolean;
  shareEmail: boolean;
  shareWhatsapp: boolean;
  copyText: boolean;
  suggestFix: boolean;
  apiAccess: boolean;
  /** Analyses API incluses par mois (null = illimité). Pertinent uniquement pour
   *  les plans avec apiAccess. Premium = 150 ; Enterprise = illimité. */
  apiMonthlyQuota: number | null;
  maxMembers: number;
  monthlyLimit: number | null;
  unlimited: boolean;
}

export const PLAN_FEATURES: Record<PlanType, PlanFeatures> = {
  free: {
    plan: 'free',
    analysesText: true,
    analysesLabel: false,
    analysesCampaign: false,
    analysesSocial: false,
    analysesCreative: false,
    analysesEvent: false,
    fullResults: true,
    history: false,
    exportPdf: false,
    shareEmail: false,
    shareWhatsapp: false,
    copyText: false,
    suggestFix: false,
    apiAccess: false,
    apiMonthlyQuota: null,
    maxMembers: 1,
    // 100% payant : aucune analyse incluse sur le plan `free`. Un nouveau compte
    // doit s'abonner (Pro/Premium) ou acheter un bundle. Le plan `free` n'est
    // qu'un état "sans abonnement" — l'ancien free tier (3 analyses/mois) a été
    // retiré le 2026-05-04. `beta` reste à 30 pour le soft-launch.
    monthlyLimit: 0,
    unlimited: false,
  },
  beta: {
    plan: 'beta',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: true,
    analysesSocial: true,
    analysesCreative: true,
    analysesEvent: true,
    fullResults: true,
    // Découverte offerte (code promo salon, testeur bêta) : on montre TOUS les
    // types d'analyse et le résultat complet, mais RIEN ne sort ni ne reste —
    // pas d'historique, pas de PDF, pas d'email, pas de copie. La bannière
    // « Analyse en lecture seule » promettait déjà ce cadre ; l'historique,
    // lui, restait ouvert. Ce qu'un abonnement débloque doit se voir.
    history: false,
    exportPdf: false,
    shareEmail: false,
    shareWhatsapp: false,
    copyText: false,
    suggestFix: true,
    apiAccess: false,
    apiMonthlyQuota: null,
    maxMembers: 1,
    monthlyLimit: 30,
    unlimited: false,
  },
  pro: {
    plan: 'pro',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: false,
    analysesSocial: false,
    analysesCreative: false,
    analysesEvent: false,
    fullResults: true,
    history: true,
    exportPdf: true,
    shareEmail: false,
    shareWhatsapp: false,
    copyText: true,
    suggestFix: true,
    apiAccess: false,
    apiMonthlyQuota: null,
    maxMembers: 1,
    monthlyLimit: null,
    unlimited: true,
  },
  premium: {
    plan: 'premium',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: true,
    analysesSocial: true,
    analysesCreative: true,
    analysesEvent: true,
    fullResults: true,
    history: true,
    exportPdf: true,
    shareEmail: true,
    shareWhatsapp: true,
    copyText: true,
    suggestFix: true,
    apiAccess: true,
    apiMonthlyQuota: 150,
    maxMembers: 3,
    monthlyLimit: null,
    unlimited: true,
  },
  enterprise: {
    plan: 'enterprise',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: true,
    analysesSocial: true,
    analysesCreative: true,
    analysesEvent: true,
    fullResults: true,
    history: true,
    exportPdf: true,
    shareEmail: true,
    shareWhatsapp: true,
    copyText: true,
    suggestFix: true,
    apiAccess: true,
    apiMonthlyQuota: null,
    maxMembers: 9999,
    monthlyLimit: null,
    unlimited: true,
  },
  // Accès illimité OFFERT (équivalent Premium, sans plafond API). Comp, pas de
  // Stripe. L'accès API reste de toute façon derrière le toggle per-org
  // (computeApiAccess exige apiAccessEnabled=true).
  partner: {
    plan: 'partner',
    analysesText: true,
    analysesLabel: true,
    analysesCampaign: true,
    analysesSocial: true,
    analysesCreative: true,
    analysesEvent: true,
    fullResults: true,
    history: true,
    exportPdf: true,
    shareEmail: true,
    shareWhatsapp: true,
    copyText: true,
    suggestFix: true,
    apiAccess: true,
    apiMonthlyQuota: null,
    maxMembers: 3,
    monthlyLimit: null,
    unlimited: true,
  },
};

/**
 * Effective API access = the plan allows it (Gate A) AND an admin has
 * explicitly toggled it on for the org (Gate B). Both must hold. Gate B fails
 * closed: anything other than the literal boolean `true` (null/undefined/false)
 * denies access. Single source of truth for the double-gate, used by both the
 * API-key middleware and the JWT auth middleware so they can never drift apart.
 */
export function computeApiAccess(
  plan: PlanType,
  apiAccessEnabled: boolean | null | undefined,
): boolean {
  return PLAN_FEATURES[plan].apiAccess && apiAccessEnabled === true;
}

// ── Pricing display config ────────────────────────────

export interface PricingTier {
  plan: PlanType;
  name: string;
  tagline: string;
  priceMonthly: number | null;  // cents EUR, null = "Sur devis"
  priceYearly: number | null;   // cents EUR per month when billed yearly
  /** Regular (post-launch) monthly price in cents. Shown as "au lieu de X €" when launchPrice=true. */
  priceMonthlyRegular?: number | null;
  /** Regular (post-launch) yearly-billed monthly-equivalent price in cents. */
  priceYearlyRegular?: number | null;
  /** When true, priceMonthly/priceYearly are launch prices and priceMonthlyRegular/priceYearlyRegular are the future regime. */
  launchPrice?: boolean;
  /** Number of months the launch price is locked in for subscribers who join during the launch window. */
  launchLockMonths?: number;
  /** Optional "from" label override when priceMonthly is null (Enterprise). */
  priceFromLabel?: string;
  highlighted: boolean;
  cta: string;
  features: string[];
  disabledFeatures: string[];
}

/**
 * ISO date (YYYY-MM-DD) after which the public launch price ends and tiers revert to their regular price.
 * Update this single constant to extend or shorten the Founding Member window.
 * Rationale: Art. L.112-1-1 Code conso imposes transparency on launch-price end date.
 */
export const LAUNCH_PRICE_ENDS_AT = '2026-12-31';

export const PRICING_TIERS: PricingTier[] = [
  {
    plan: 'pro',
    name: 'Pro',
    tagline: 'Pour les professionnels du secteur',
    priceMonthly: 4900,
    priceYearly: 3900,
    priceMonthlyRegular: 6900,
    priceYearlyRegular: 5500,
    launchPrice: true,
    launchLockMonths: 24,
    highlighted: true,
    cta: 'Passer au Pro',
    features: [
      'Analyses texte illimitées',
      'Analyse d\'étiquettes illimitée',
      'Résultat complet + sources',
      'Historique illimité',
      'Export PDF',
      'Copie du texte',
      '1 utilisateur',
    ],
    disabledFeatures: [
      'Analyse de posts réseaux sociaux',
      'Analyse de campagnes pub',
      'Partage rapport HTML par email',
      'Accès API & MCP',
    ],
  },
  {
    plan: 'premium',
    name: 'Premium',
    tagline: 'L\'arsenal complet pour agences et marques',
    priceMonthly: 12900,
    priceYearly: 9900,
    priceMonthlyRegular: 17900,
    priceYearlyRegular: 13900,
    launchPrice: true,
    launchLockMonths: 24,
    highlighted: false,
    cta: 'Passer au Premium',
    features: [
      'Tout le plan Pro +',
      'Analyse de posts réseaux sociaux',
      'Analyse de campagnes publicitaires',
      'Partage rapport HTML par email',
      'Accès API REST & MCP',
      'Jusqu\'à 3 utilisateurs',
      'Support prioritaire',
    ],
    disabledFeatures: [],
  },
  {
    plan: 'enterprise',
    name: 'Entreprise',
    tagline: 'Pour les équipes de 5+ utilisateurs',
    priceMonthly: null,
    priceYearly: null,
    highlighted: false,
    cta: 'Nous contacter',
    features: [
      'Tout le plan Premium +',
      'Facturation consolidée multi-utilisateurs',
      'SLA garanti 99.9%',
      'Support prioritaire (réponse < 4h)',
      'Account manager dédié',
    ],
    disabledFeatures: [],
  },
];

// ── Bundles (thematic credit packs) ───────────────────
// A bundle bundles N credits, each line either type-specific
// (Pack Réseaux Sociaux = 10 social_post) or category-restricted
// (Pack Test = 2 credits valid for brand_name, slogan or concept).
// On purchase the webhook creates one row in `org_credits` per credit line.

export type BundleId =
  | 'pack_test'
  | 'pack_social'
  | 'pack_event'
  | 'pack_lancement'
  | 'pack_campagne';

export interface BundleCreditLine {
  /** When set: credits valid only for this AnalysisType. Mutually exclusive with `allowedTypes`. */
  analysisType?: AnalysisType;
  /** When set: credits valid for any AnalysisType in the list. */
  allowedTypes?: AnalysisType[];
  /** Number of credits this line grants. */
  credits: number;
  /** User-facing label, e.g. "1 analyse de marque". */
  label: string;
}

export interface Bundle {
  id: BundleId;
  name: string;
  tagline: string;
  priceCents: number;
  /** Mono = single persona / single feature. Multi = project with several features. */
  family: 'mono' | 'multi';
  /** Sum of credits across all lines (for marketing copy: "5 analyses incluses"). */
  totalCredits: number;
  creditLines: BundleCreditLine[];
  /** UI ordering hint within its family. */
  displayOrder: number;
}

export const BUNDLES: Bundle[] = [
  // ── Mono-feature ─────────────────────────────────────
  {
    id: 'pack_test',
    name: 'Pack Test',
    tagline: 'Évaluez la qualité avant de vous engager',
    priceCents: 2900,
    family: 'mono',
    totalCredits: 2,
    displayOrder: 1,
    creditLines: [
      {
        allowedTypes: ['general_question', 'brand_name', 'slogan', 'concept'],
        credits: 2,
        label: '2 analyses textes au choix (question générale, marque, slogan ou concept)',
      },
    ],
  },
  {
    id: 'pack_social',
    name: 'Pack Réseaux Sociaux',
    tagline: 'Pour les community managers et équipes social media',
    priceCents: 9900,
    family: 'mono',
    totalCredits: 10,
    displayOrder: 2,
    creditLines: [
      {
        analysisType: 'social_post',
        credits: 10,
        label: '10 audits de posts réseaux sociaux',
      },
    ],
  },
  {
    id: 'pack_event',
    name: 'Pack Événement',
    tagline: 'Pour vos lancements, salons et opérations CHR/GMS',
    priceCents: 8900,
    family: 'mono',
    totalCredits: 3,
    displayOrder: 3,
    creditLines: [
      {
        analysisType: 'event_activation',
        credits: 3,
        label: '3 audits d\'opération (événement, PLV, dégustation, goodies)',
      },
    ],
  },
  // ── Multi-feature ────────────────────────────────────
  {
    id: 'pack_lancement',
    name: 'Pack Lancement',
    tagline: 'Audit complet d\'un lancement produit : une analyse de chaque type',
    priceCents: 14900,
    family: 'multi',
    totalCredits: 8,
    displayOrder: 4,
    creditLines: [
      { analysisType: 'general_question', credits: 1, label: '1 question générale sur la conformité' },
      { analysisType: 'brand_name', credits: 1, label: '1 analyse de nom de marque' },
      { analysisType: 'slogan', credits: 1, label: '1 analyse de slogan' },
      { analysisType: 'concept', credits: 1, label: '1 audit de concept' },
      { analysisType: 'label', credits: 1, label: '1 analyse d\'étiquette' },
      { analysisType: 'campaign', credits: 1, label: '1 analyse de campagne' },
      { analysisType: 'social_post', credits: 1, label: '1 audit de post réseaux sociaux' },
      { analysisType: 'event_activation', credits: 1, label: '1 audit d\'événement / PLV' },
    ],
  },
  {
    id: 'pack_campagne',
    name: 'Pack Campagne',
    tagline: 'Pour les campagnes marketing lourdes (saisonnier, lancement majeur)',
    priceCents: 19900,
    family: 'multi',
    totalCredits: 9,
    displayOrder: 5,
    creditLines: [
      { analysisType: 'campaign', credits: 3, label: '3 analyses de campagne' },
      { analysisType: 'social_post', credits: 5, label: '5 audits de posts réseaux sociaux' },
      { analysisType: 'event_activation', credits: 1, label: '1 audit d\'événement' },
    ],
  },
];

export function getBundleById(id: BundleId): Bundle | undefined {
  return BUNDLES.find((b) => b.id === id);
}

/** « 1 crédit » / « 9 crédits ». */
export function creditCountLabel(count: number): string {
  return `${count} crédit${count > 1 ? 's' : ''}`;
}

/** Contenu d'un pack en une ligne, tiré de ses lignes de crédit (source unique
 *  BUNDLES) : « 2 analyses textes au choix (question générale, marque, slogan ou concept) », ou
 *  « 3 analyses de campagne, 5 audits de posts réseaux sociaux, 1 audit
 *  d'événement ». Sert au message de confirmation après achat. */
export function bundleContentsLabel(bundle: Bundle): string {
  return bundle.creditLines.map((line) => line.label).join(', ');
}

/** Phrase de confirmation d'un pack crédité : « 2 crédits sont ajoutés à votre
 *  compte : 2 analyses textes au choix (question générale, marque, slogan ou concept). » */
export function bundleCreditedMessage(bundle: Bundle): string {
  const n = bundle.totalCredits;
  const verb = n > 1 ? 'sont ajoutés' : 'est ajouté';
  return `${creditCountLabel(n)} ${verb} à votre compte : ${bundleContentsLabel(bundle)}.`;
}

/** Maps each analysis type to the PlanFeatures flag that grants it. */
export const ANALYSIS_TYPE_FEATURE: Record<AnalysisType, keyof PlanFeatures> = {
  general_question: 'analysesText',
  brand_name: 'analysesText',
  slogan: 'analysesText',
  concept: 'analysesText',
  label: 'analysesLabel',
  campaign: 'analysesCampaign',
  social_post: 'analysesSocial',
  creative_brief: 'analysesCreative',
  event_activation: 'analysesEvent',
};

/** All analysis types a bundle's credits can be spent on. */
export function bundleAnalysisTypes(bundle: Bundle): AnalysisType[] {
  const set = new Set<AnalysisType>();
  for (const line of bundle.creditLines) {
    if (line.analysisType) set.add(line.analysisType);
    if (line.allowedTypes) for (const t of line.allowedTypes) set.add(t);
  }
  return [...set];
}

/** A bundle is relevant to a plan only when EVERY credit it grants targets an
 *  analysis type the plan does NOT already cover with unlimited access. So a
 *  subscriber never sees a bundle made (partly) redundant by their plan:
 *  Premium (unlimited everything) sees none; Pro sees only the packs whose
 *  features it lacks (social, événement, campagne); free / no-subscription see all. */
export function isBundleRelevantForFeatures(bundle: Bundle, features: PlanFeatures): boolean {
  const covered = (t: AnalysisType) =>
    features.unlimited && features[ANALYSIS_TYPE_FEATURE[t]] === true;
  return bundleAnalysisTypes(bundle).every((t) => !covered(t));
}

// ── Crédits utilisables par type d'analyse ────────────
// Une ligne `org_credits` s'applique à un type si son analysis_type est ce
// type, si le type figure dans allowed_types, ou si elle est générique (ni
// l'un ni l'autre). Réplique EXACTE de CREDIT_APPLIES_SQL
// (pocketbase/pb_hooks/credits_lib.js), qui décide à la consommation : le
// compteur affiché et le débit réel ne doivent jamais diverger.

export interface CreditLineLike {
  analysis_type: string | null;
  allowed_types: string[] | null;
  credits_remaining: number | null;
}

export function creditLineAppliesTo(line: CreditLineLike, type: AnalysisType): boolean {
  // PocketBase stocke un texte vide en '' (jamais NULL) : le test de vérité
  // couvre les deux formes, comme le SQL.
  const allowed = Array.isArray(line.allowed_types) ? line.allowed_types : [];
  if (line.analysis_type && line.analysis_type === type) return true;
  if (allowed.includes(type)) return true;
  return !line.analysis_type && allowed.length === 0;
}

/** Crédits restants utilisables pour chaque type d'analyse. Une même ligne
 *  compte pour chaque type qu'elle couvre (Pack Test : 2 crédits pour marque,
 *  2 pour slogan, 2 pour concept), le total réel restant la somme des lignes. */
export function creditsByType(lines: CreditLineLike[]): Partial<Record<AnalysisType, number>> {
  const byType: Partial<Record<AnalysisType, number>> = {};
  for (const line of lines) {
    const n = line.credits_remaining ?? 0;
    if (n <= 0) continue;
    for (const { key } of ANALYSIS_OPTIONS) {
      if (creditLineAppliesTo(line, key)) byType[key] = (byType[key] ?? 0) + n;
    }
  }
  return byType;
}

/**
 * État d'une carte d'analyse sur l'écran d'accueil, dans l'ordre où le serveur
 * décide de l'accès (requireAnalysisAccess) :
 *   - `unlimited` : le plan couvre ce type sans limite, aucun compteur ;
 *   - `quota`     : le plan couvre ce type et le quota offert (free/beta) n'est
 *                   pas épuisé, le bandeau d'en-tête en donne le reste ;
 *   - `credits`   : des crédits achetés paient ce type (`count` > 0) ;
 *   - `no_credit` : le plan couvre ce type, mais il ne reste ni quota ni crédit
 *                   (ex. plan sans abonnement, ou analyses offertes épuisées) ;
 *   - `locked`    : ni le plan ni un crédit ne couvrent ce type.
 */
export type AnalysisTileAccess =
  | { kind: 'unlimited' }
  | { kind: 'quota' }
  | { kind: 'credits'; count: number }
  | { kind: 'no_credit' }
  | { kind: 'locked' };

export function analysisTileAccess(params: {
  type: AnalysisType;
  features: PlanFeatures;
  /** Crédits utilisables pour ce type (Organization.creditsPerType). */
  credits: number;
  /** Analyses offertes restantes du plan free/beta (0 pour les autres plans). */
  quotaRemaining: number;
}): AnalysisTileAccess {
  const { type, features, credits, quotaRemaining } = params;
  const planGrants = features[ANALYSIS_TYPE_FEATURE[type]] === true;
  if (planGrants && features.unlimited) return { kind: 'unlimited' };
  if (planGrants && quotaRemaining > 0) return { kind: 'quota' };
  if (credits > 0) return { kind: 'credits', count: credits };
  if (planGrants) return { kind: 'no_credit' };
  return { kind: 'locked' };
}

/** Crédits consommés par une analyse. Un carrousel social coûte par paliers selon
 *  le nombre de slides (2-3 → 2, 4-6 → 3, 7+ → 4) ; tout le reste coûte 1 crédit.
 *  Source UNIQUE partagée client (compteur live) ↔ serveur (débit). */
export function analysisCreditCost(
  type: AnalysisType,
  socialFormat: string | undefined,
  imageCount: number,
): number {
  if (type === 'social_post' && socialFormat === 'carousel') {
    return imageCount <= 3 ? 2 : imageCount <= 6 ? 3 : 4;
  }
  return 1;
}

// ── Trademark availability (admin-only pre-screen) ────
// Pre-screen IA branchée sur INPI (FR + EUTM en vigueur + WIPO désignant FR)
// et EUIPO (EUTM en cours d'examen + déposées). NE remplace PAS un avis CPI :
// indicateur de risque, pas verdict juridique. Voir docs/audit-cnil-rgpd-mvp.md
// pour le cadrage liability.

/** Classes Nice les plus utiles pour les marques boisson + écosystème SaaS. */
export type NiceClass =
  | 9   // Logiciels, applis mobiles, supports numériques
  | 25  // Vêtements, chaussures (merch boisson)
  | 29  // Aliments d'origine animale, produits laitiers, conserves
  | 30  // Café, thé, sucreries, sauces, épicerie sèche
  | 32  // Bières, eaux, sodas, boissons sans alcool
  | 33  // Vins, spiritueux, boissons alcoolisées (sauf bière)
  | 35  // Publicité, gestion d'affaires, e-commerce, services SaaS B2B
  | 41  // Éducation, événementiel, divertissement
  | 42  // Conception logicielle, SaaS technique, recherche scientifique
  | 43  // Restauration, hôtellerie, débits de boissons
  | 45; // Services juridiques

export interface NiceClassOption {
  value: NiceClass;
  label: string;
  /** Short hint about the typical product/service this class covers. */
  description: string;
  /** Mark the class as a default pick for the beverage market. */
  beverageRelevant?: boolean;
}

export const NICE_CLASS_OPTIONS: NiceClassOption[] = [
  { value: 32, label: 'Classe 32 — Bières, sodas, eaux',                description: 'Bières, eaux minérales, jus, sodas, boissons sans alcool',                beverageRelevant: true },
  { value: 33, label: 'Classe 33 — Vins, spiritueux',                  description: 'Vins, champagnes, spiritueux, boissons alcoolisées (hors bière)',         beverageRelevant: true },
  { value: 35, label: 'Classe 35 — Vente, marketing, e-commerce',      description: 'Publicité, gestion commerciale, e-commerce, services SaaS B2B' },
  { value: 43, label: 'Classe 43 — Restauration, débits de boissons',  description: 'Bars, restaurants, hôtels, services de dégustation',                       beverageRelevant: true },
  { value: 41, label: 'Classe 41 — Événementiel, formation',           description: 'Événements, salons, formation, divertissement' },
  { value: 30, label: 'Classe 30 — Épicerie, café, sucreries',         description: 'Café, thé, biscuiterie, sauces, épicerie sèche' },
  { value: 29, label: 'Classe 29 — Aliments, conserves',               description: 'Viande, poisson, conserves, produits laitiers' },
  { value: 25, label: 'Classe 25 — Vêtements, merch',                  description: 'Vêtements, chaussures, accessoires textiles (goodies marque)' },
  { value: 9,  label: 'Classe 9 — Logiciels, applis',                  description: 'Logiciels, applis mobiles, plateformes numériques' },
  { value: 42, label: 'Classe 42 — SaaS technique',                    description: 'Conception logicielle, hébergement, R&D scientifique' },
  { value: 45, label: 'Classe 45 — Services juridiques',               description: 'Conseil juridique, propriété intellectuelle, services personnels' },
];

export type TrademarkSource = 'INPI' | 'EUIPO';

/** Type of similarity hit between the queried name and an existing mark. */
export type TrademarkMatchKind =
  | 'exact'       // Identical normalized string
  | 'quasi'       // Levenshtein ≤ 2 on normalized string
  | 'phonetic'    // Same phonetic key (French Métaphone simplifié)
  | 'substring'   // Queried name is contained in / contains the mark name
  | 'related';    // Loose match returned by upstream API but no strong rule fired

/** Final risk verdict for the whole search (not for a single hit). */
export type TrademarkRiskVerdict =
  | 'GREEN'    // Aucun match fort en classes pertinentes
  | 'AMBER'    // Matchs phonétiques/quasi-identiques OU exact en classe non pertinente
  | 'RED'      // Match exact ou quasi-identique en classe pertinente
  | 'UNKNOWN'; // Aucune source de données n'a pu être interrogée (creds manquants ou erreurs upstream)

export interface TrademarkOwner {
  name?: string;
  country?: string;
}

export interface TrademarkHit {
  source: TrademarkSource;
  /** Office-specific identifier (INPI registration number or EUIPO file number). */
  registrationNumber: string;
  /** Mark wording (mots verbaux) as recorded by the office. */
  markName: string;
  /** Status as reported upstream (e.g. "ENREGISTREE", "DEPOSEE", "REJETEE"). */
  status?: string;
  /** Filing or registration date if available (ISO YYYY-MM-DD). */
  filingDate?: string;
  /** Nice classes covered by this mark. */
  niceClasses: NiceClass[];
  owner?: TrademarkOwner;
  /** Public URL on the office's website to view the full record. */
  publicUrl?: string;
  /** Type of similarity between the query and this hit. */
  matchKind: TrademarkMatchKind;
  /** Similarity score 0-100 (higher = closer to the queried name). */
  similarityScore: number;
  /** Subset of the queried classes that this hit overlaps. */
  overlappingClasses: NiceClass[];
}

export interface TrademarkSearchInput {
  /** The brand name being checked. */
  brandName: string;
  /** Nice classes the user wants to file in. Required (min 1). */
  niceClasses: NiceClass[];
}

export interface TrademarkSearchResult {
  query: TrademarkSearchInput;
  /** Aggregated risk based on hits + overlapping classes. */
  verdict: TrademarkRiskVerdict;
  /** Plain-French explanation of the verdict for the UI. */
  verdictExplanation: string;
  /** Per-source health: false when API creds missing or call failed. */
  sourceStatus: Record<TrademarkSource, { ok: boolean; message?: string }>;
  /** All hits, sorted by similarity desc then by overlapping class count. */
  hits: TrademarkHit[];
  /** Stats for the UI summary card. */
  stats: {
    totalHits: number;
    exactHits: number;
    quasiHits: number;
    phoneticHits: number;
    overlappingClassesCount: number;
  };
  /** Disclaimer to display under any result. */
  disclaimer: string;
  /** ISO timestamp of the search. */
  searchedAt: string;
}

// ── Usage event (for analytics) ───────────────────────

export interface UsageEvent {
  id: string;
  organizationId: string;
  userId: string;
  analysisType: AnalysisType;
  planAtTime: PlanType;
  creditSource: 'subscription' | 'free_tier' | 'one_time_purchase';
  createdAt: string;
}

// ── App State ───────────────────────────────────────────

export type AppStep = 'select' | 'upload' | 'loading' | 'result' | 'pricing' | 'admin' | 'historique' | 'account';
